import { Question, SubQuestion } from 'src/types/helios/Question';
import { AuditQuestionItemResponse } from 'src/types/helios/QuestionResponse';
import { getQuestionContent } from 'src/helper/questionHelper';
import { QuestionResultType } from 'src/contstant/commonLabels';

export interface MandatoryFailViolation {
  questionId: string;
  subQuestionId?: string;
  missingComment: boolean;
  missingEvidence: boolean;
  missingNaReason: boolean;
}

/**
 * Checks if a question or any of its sub-questions has missing mandatory
 * comment/evidence for a FAIL response, or a missing NA reason for an NA response.
 */
export function getQuestionViolations(
  question: Question,
  response: AuditQuestionItemResponse | undefined,
  naReasonsExist?: boolean,
): MandatoryFailViolation[] {
  const violations: MandatoryFailViolation[] = [];
  if (!response) return violations;

  // Check question-level requirements for FAIL / PASS
  if (response.responseResult === 'FAIL' || response.responseResult === 'PASS') {
    const isPass = response.responseResult === 'PASS';
    const requireComment = isPass
      ? !!question.requireCommentOnPass
      : !!question.requireCommentOnFail;
    const requireEvidence = isPass
      ? !!question.requireEvidenceOnPass
      : !!question.requireEvidenceOnFail;

    const missingComment = requireComment && !response.response?.comment?.trim();
    const missingEvidence =
      requireEvidence &&
      (!response.response?.mediaIds || response.response.mediaIds.length === 0);

    if (missingComment || missingEvidence) {
      violations.push({
        questionId: question.questionId,
        missingComment,
        missingEvidence,
        missingNaReason: false,
      });
    }
  }

  // Check NA reason violations
  // The UI hides sub-question dropdowns when main has a reason, and hides
  // main dropdown when all NA sub-questions have reasons. Validation mirrors that.
  if (naReasonsExist) {
    const mainIsNa = response.responseResult === 'NA';
    const mainHasNaReason = !!response.response?.naReasonId;
    const naSubResponses = (response.subResponses ?? []).filter(
      (sub) => sub.result === 'NA',
    );
    const allNaSubsHaveReason =
      naSubResponses.length > 0 && naSubResponses.every((sub) => !!sub.naReasonId);

    // Main question: flag if NA, no reason, and subs don't cover it
    if (mainIsNa && !mainHasNaReason && !allNaSubsHaveReason) {
      violations.push({
        questionId: question.questionId,
        missingComment: false,
        missingEvidence: false,
        missingNaReason: true,
      });
    }

    // Sub-questions: flag only if main doesn't have a reason AND main is not NA
    // (when main is NA, the main-level violation already covers this case)
    if (!mainHasNaReason && !mainIsNa) {
      for (const subResp of naSubResponses) {
        if (!subResp.naReasonId) {
          violations.push({
            questionId: question.questionId,
            subQuestionId: subResp.subQuestionId,
            missingComment: false,
            missingEvidence: false,
            missingNaReason: true,
          });
        }
      }
    }
  }

  // Check sub-question-level requirements independently
  const subQuestions = getQuestionContent(question)?.subQuestions;
  if (subQuestions && response.subResponses) {
    const subQuestionMap = new Map<string, SubQuestion>();
    for (const sq of subQuestions) {
      if (sq.subQuestionId) subQuestionMap.set(sq.subQuestionId, sq);
    }

    for (const subResp of response.subResponses) {
      // Check FAIL / PASS violations
      if (subResp.result === 'FAIL' || subResp.result === 'PASS') {
        const subQ = subQuestionMap.get(subResp.subQuestionId);
        if (!subQ) continue;

        const isPass = subResp.result === 'PASS';
        const requireComment = isPass
          ? !!subQ.requireCommentOnPass
          : !!subQ.requireCommentOnFail;
        const requireEvidence = isPass
          ? !!subQ.requireEvidenceOnPass
          : !!subQ.requireEvidenceOnFail;

        const missingComment = requireComment && !subResp.comment?.trim();
        const missingEvidence =
          requireEvidence && (!subResp.mediaIds || subResp.mediaIds.length === 0);

        if (missingComment || missingEvidence) {
          violations.push({
            questionId: question.questionId,
            subQuestionId: subResp.subQuestionId,
            missingComment,
            missingEvidence,
            missingNaReason: false,
          });
        }
      }
    }
  }

  return violations;
}

/**
 * Checks all questions in an audit for mandatory fail requirement violations.
 * Returns true if any violations exist.
 */
export function hasAuditMandatoryFailViolations(
  questions: Question[],
  responses: AuditQuestionItemResponse[],
): boolean {
  const responseMap = new Map(responses.map((r) => [r.questionId, r]));
  return questions.some((question) => {
    const response = responseMap.get(question.questionId);
    return getQuestionViolations(question, response).length > 0;
  });
}

/**
 * Returns a user-friendly message describing what's missing.
 */
export function getViolationMessage(violations: MandatoryFailViolation[]): string {
  const commentCount = violations.filter((v) => v.missingComment).length;
  const evidenceCount = violations.filter((v) => v.missingEvidence).length;
  const naReasonCount = violations.filter((v) => v.missingNaReason).length;
  const parts: string[] = [];
  if (commentCount > 0) parts.push(`${commentCount} missing comment(s)`);
  if (evidenceCount > 0) parts.push(`${evidenceCount} missing attachment(s)`);
  if (naReasonCount > 0) parts.push(`${naReasonCount} missing Not Applicable reason(s)`);
  return `Audit cannot be closed. Mandatory inputs are missing: ${parts.join(', ')}. Please complete before closing the audit.`;
}

/**
 * Checks if any NA-answered questions/sub-questions are missing a required NA reason.
 * Only applies when NA reasons are configured for the division (naReasons list is non-empty).
 * Rules:
 *  - If main question has naReasonId → sub-questions are satisfied (NA/All case)
 *  - If all NA sub-questions have naReasonId → main question is satisfied
 * Returns true if any violations exist.
 */
export function hasAuditMissingNaReasonViolations(
  responses: AuditQuestionItemResponse[],
  naReasonsExist: boolean,
): boolean {
  if (!naReasonsExist) return false;

  return responses.some((response) => {
    const mainIsNa = response.responseResult === 'NA';
    const mainHasNaReason = !!response.response?.naReasonId;

    // If main has a reason, everything is satisfied for this question
    if (mainHasNaReason) return false;

    const naSubResponses = (response.subResponses ?? []).filter(
      (sub) => sub.result === 'NA',
    );
    const allNaSubsHaveReason =
      naSubResponses.length > 0 && naSubResponses.every((sub) => !!sub.naReasonId);

    // Main is NA with no reason, and subs don't cover it
    if (mainIsNa && !allNaSubsHaveReason) return true;

    // Sub-questions missing reasons (only when main doesn't have one)
    if (naSubResponses.some((sub) => !sub.naReasonId)) return true;

    return false;
  });
}

/**
 * Checks if a specific sub-question has a violation.
 */
export function hasSubQuestionViolation(
  violations: MandatoryFailViolation[],
  subQuestionId: string,
): boolean {
  return violations.some((v) => v.subQuestionId === subQuestionId);
}

/**
 * Checks if a question has a question-level violation (not sub-question).
 */
export function hasQuestionLevelViolation(violations: MandatoryFailViolation[]): boolean {
  return violations.some((v) => !v.subQuestionId);
}

/**
 * Returns the appropriate warning tooltip for a question based on its violations.
 */
export function getWarningTooltip(violations: MandatoryFailViolation[]): string {
  const hasQLevel = hasQuestionLevelViolation(violations);
  const hasSQLevel = violations.some((v) => v.subQuestionId);
  const hasNaReason = violations.some((v) => v.missingNaReason);
  const hasFailIssues = violations.some((v) => v.missingComment || v.missingEvidence);

  if (hasNaReason && hasFailIssues) {
    return 'Required inputs missing: comment/attachment for failed question(s) and Not Applicable reason selection.';
  }

  if (hasNaReason && !hasFailIssues) {
    return 'Not Applicable reason is required. Please select a Not Applicable reason before closing the audit.';
  }

  if (hasQLevel && hasSQLevel) {
    return 'Required inputs missing for this question and its sub-question(s). Select to see details.';
  } else if (hasQLevel) {
    return 'Comment and/or attachment required for this failed question.';
  }
  return 'Required inputs missing for a failed sub-question. Select to see details.';
}

/**
 * Returns a field label with (Required) or (Optional) suffix based on
 * whether the requirement flag is set and the response is FAIL.
 */
export function getFieldRequirementLabel(
  fieldName: string,
  isRequired?: boolean,
  isFail?: boolean,
): string {
  return `${fieldName} ${isRequired && isFail ? '(Required)' : '(Optional)'}`;
}

/**
 * Determines if the NA reason dropdown should be visible for a given target.
 *
 * Rules:
 * - Main dropdown: always show for NA/All; hide when all NA subs have reasons.
 * - Sub dropdown: hide when main is NA/All or main already has a reason.
 */
export function shouldShowNaReasonDropdown(
  target: 'main' | 'sub',
  opts: {
    mainResponseResult?: string;
    mainNaReasonId?: string;
    localMainNaReason?: { reasonId: string; label: string };
    subResponseResult?: string;
    subNaReasonId?: string;
    subResponses?: Array<{ subQuestionId: string; result?: string; naReasonId?: string }>;
    localNaReasons?: Record<string, { reasonId: string; label: string }>;
  },
): boolean {
  const isNaAll = opts.mainResponseResult === QuestionResultType.NA;

  if (target === 'sub') {
    if (opts.subResponseResult !== QuestionResultType.NA) return false;
    // If this sub-question already has a reason selected (local or saved), always show it
    if (opts.subNaReasonId) return true;
    // Hide sub dropdowns when user explicitly clicked "NA All" on main question
    if (isNaAll) return false;
    // Hide if main already has a reason (cascades to cover all subs)
    if (opts.mainNaReasonId || opts.localMainNaReason) return false;
    return true;
  }

  // target === 'main'
  if (isNaAll) return true;

  const naSubResponses = (opts.subResponses ?? []).filter(
    (s) => s.result === QuestionResultType.NA,
  );
  if (naSubResponses.length === 0) return true;

  const localReasons = opts.localNaReasons ?? {};
  const allSubsCovered = naSubResponses.every(
    (s) => !!s.naReasonId || !!localReasons[s.subQuestionId],
  );
  return !allSubsCovered;
}
